import React, { createContext, useContext, useEffect, useState } from "react";
import { computeSlugPricing } from "../lib/pricing";

const CartContext = createContext(null);
const STORAGE_KEY = "signapharma_cart";
// Marqueur de session : posé à la 1ère ouverture du site dans cette session
// navigateur. Vidé automatiquement à la fermeture de l'onglet/navigateur.
// Tant qu'il est absent, on démarre avec un panier VIDE (demande client :
// « vider le panier à l'ouverture » pour éviter que le client retrouve un
// panier abandonné et achète par erreur d'anciens articles).
const SESSION_FLAG = "signapharma_cart_session";

/**
 * Ré-hydrate un item de panier en calculant son unit_price_chf si absent.
 * Utilisé à la fois par addItem() et par le hydrating initial depuis
 * localStorage (items sauvegardés AVANT que le fix soit en place, ou
 * items ajoutés depuis un flow qui oublie de passer unit_price_chf).
 */
function enrichItemWithPrice(item) {
  if (!item || typeof item !== "object") return item;
  if (typeof item.unit_price_chf === "number" && item.unit_price_chf > 0) {
    return item;
  }
  try {
    const registry =
      (typeof window !== "undefined" &&
        window.__SIGNAPHARMA_CROSS_DESIGNS__) || [];
    const designsById = {};
    for (const d of registry) designsById[d.id] = d;
    const pricing = computeSlugPricing({
      slug: item.product_slug,
      cfg: item.configuration || {},
      designsById,
    });
    if (pricing && pricing.totalCHF > 0) {
      return { ...item, unit_price_chf: pricing.totalCHF };
    }
  } catch {
    // silencieux
  }
  return item;
}

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const hasSession = sessionStorage.getItem(SESSION_FLAG);
      if (!hasSession) {
        // Première ouverture de la session navigateur → panier vidé.
        sessionStorage.setItem(SESSION_FLAG, "1");
        localStorage.removeItem(STORAGE_KEY);
        return [];
      }
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Ré-enrichit les items qui n'ont pas de unit_price_chf (typiquement des
  // items ajoutés avant que le fix soit en place, ou depuis un flow qui a
  // oublié de le passer). Le registre des designs de croix est peuplé de
  // manière asynchrone (après mount du CrossConfigurator ou navigation vers
  // une page produit), donc on tente aussi après un délai.
  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;
    const needsEnrich = items.some(
      (it) => typeof it.unit_price_chf !== "number" || it.unit_price_chf <= 0,
    );
    if (!needsEnrich) return;
    const tryEnrich = () => {
      setItems((prev) => {
        let changed = false;
        const next = prev.map((it) => {
          if (typeof it.unit_price_chf === "number" && it.unit_price_chf > 0) return it;
          const enriched = enrichItemWithPrice(it);
          if (enriched !== it) changed = true;
          return enriched;
        });
        return changed ? next : prev;
      });
    };
    // Essai immédiat (registre peut être déjà chargé)
    tryEnrich();
    // Essai différé (attend le mount des configurateurs)
    const t1 = setTimeout(tryEnrich, 800);
    const t2 = setTimeout(tryEnrich, 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [items.length]);

  const addItem = (item) => {
    // item: { product_slug, product_name, image, configuration, [unit_price_chf] }
    const lineId = `${item.product_slug}-${Date.now()}`;
    const enriched = enrichItemWithPrice({
      ...item,
      lineId,
      quantity: item.quantity || 1,
    });
    setItems((prev) => [...prev, enriched]);
    setIsOpen(true);
  };

  const removeItem = (lineId) => {
    setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  };

  const updateQty = (lineId, quantity) => {
    setItems((prev) =>
      prev.map((i) => (i.lineId === lineId ? { ...i, quantity: Math.max(1, quantity) } : i))
    );
  };

  const clear = () => setItems([]);

  const totalItems = items.reduce((s, i) => s + (i.quantity || 1), 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, totalItems, isOpen, setIsOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
